/**
 * v1 Edit Allowed Domain Dialog Component
 * Modal for editing existing domain policies
 */

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { v1AllowedDomainsApi, AllowedDomain } from '../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TagInput } from '@/components/ui/tag-input'
import { Shield, AlertCircle, Edit } from 'lucide-react'

interface EditAllowedDomainDialogProps {
  open: boolean
  policy: AllowedDomain | null
  onClose: () => void
}

export function EditAllowedDomainDialog({ open, policy, onClose }: EditAllowedDomainDialogProps) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '',
    domains: [] as string[],
  })
  const [error, setError] = useState('')

  // Populate form when policy changes
  useEffect(() => {
    if (policy) {
      setForm({
        name: policy.name,
        domains: [...policy.allowedDomains],
      })
      setError('')
    }
  }, [policy])

  const updateMutation = useMutation({
    mutationFn: (data: any) => {
      if (!policy?._id) {
        throw new Error('Policy ID is missing')
      }
      return v1AllowedDomainsApi.updateAllowedDomain(policy._id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowedDomains'] })
      toast.success('Policy updated successfully')
      handleClose()
    },
    onError: (err: any) => {
      toast.error(`Failed to update policy: ${err.message || 'Unknown error'}`)
    },
  })

  const handleClose = () => {
    setForm({ name: '', domains: [] })
    setError('')
    onClose()
  }

  const handleSubmit = () => {
    setError('')

    if (!policy?._id) {
      setError('Policy ID is missing')
      return
    }

    if (!form.name.trim()) {
      setError('Policy name is required')
      return
    }

    if (form.domains.length === 0) {
      setError('At least one domain is required')
      return
    }

    updateMutation.mutate({
      name: form.name,
      allowedDomains: form.domains,
      isActive: policy.isActive, // Preserve the current status
    })
  }

  if (!policy) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-primary" />
            Edit Domain Policy
          </DialogTitle>
          <DialogDescription>
            Update the policy name and allowed domains for SaifChat user registration
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-name">Policy Name</Label>
            <Input
              id="edit-name"
              placeholder="e.g., Company Employees, Partner Organizations"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-domains">Allowed Domains</Label>
            <TagInput
              value={form.domains}
              onChange={(domains) => setForm({ ...form, domains })}
              placeholder="Enter domain and press space or enter..."
              disabled={updateMutation.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
